import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { resolveNewsApiLang } from '@formosa/shared/utils/newsLang';
import {
  handleOfflineCustomerServiceContent,
  navigateCustomerServiceContent,
} from '@formosa/shared/utils/customerServiceNavigation';
import { fetchCustomerServiceChannelMode } from '@formosa/shared/utils/customerServiceEntry';
import AuthSubPageShell from '../../components/auth/AuthSubPageShell';
import { useAuth } from '../../auth/AuthContext';
import useRequireAuth from '../../hooks/useRequireAuth';
import { useTranslation } from '../../i18n/LocaleContext';
import '../../styles/supportChat.css';

function flattenCustomerServices(groups) {
  const items = [];
  (groups || []).forEach((group) => {
    (group.details || []).forEach((detail) => {
      items.push({
        ...detail,
        serviceType: detail.type ?? group.type,
      });
    });
  });
  return items;
}

/**
 * 會員「客服中心」唯一 hub（§51.3）：依 support.mode 三態渲染。
 * /chat/support 僅由此 CTA 進入，不是並列入口。
 */
export default function CustomerServicePage() {
  const { locale, t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [centerImage, setCenterImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [channel, setChannel] = useState({
    chatEnabled: false,
    supportMode: 'OFF',
    channelMode: 'OFF',
    formosaImEnabled: false,
    thirdPartyCmsEnabled: false,
  });

  const displayItems = useMemo(() => flattenCustomerServices(items), [items]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const lang = resolveNewsApiLang(locale);
        const mode = await fetchCustomerServiceChannelMode();
        const supportMode = mode.supportMode || mode.channelMode || 'OFF';
        const needCms = mode.thirdPartyCmsEnabled || supportMode === 'THIRD_PARTY';
        const needImage =
          needCms || supportMode === 'OFF' || (!mode.formosaImEnabled && !needCms);

        const [list, imageConfig] = await Promise.all([
          needCms ? memberApi.customerServiceCenterList({ lang }) : Promise.resolve([]),
          needImage ? memberApi.customerServiceCenterImage({ lang }) : Promise.resolve(null),
        ]);
        if (!cancelled) {
          setItems(Array.isArray(list) ? list : []);
          setCenterImage(imageConfig?.imageUrl || '');
          setChannel(mode);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('support.loadFailed'));
          setItems([]);
          setCenterImage('');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [locale, t]);

  async function handleContact(item) {
    if (navigateCustomerServiceContent(item)) {
      return;
    }
    await handleOfflineCustomerServiceContent(item, {
      onCopied: () => window.alert(t('support.copied')),
      onFallback: (body) => window.alert(body),
    });
  }

  function startImSupport() {
    if (!isAuthenticated) {
      requireAuth('/chat/support');
      return;
    }
    navigate('/chat/support');
  }

  const supportMode = channel.supportMode || channel.channelMode || 'OFF';
  const showIm = channel.formosaImEnabled || supportMode === 'FORMOSA_IM';
  const showCms = channel.thirdPartyCmsEnabled || supportMode === 'THIRD_PARTY';
  const showOff = !loading && !showIm && !showCms;

  return (
    <AuthSubPageShell title={t('support.title')}>
      {loading ? <p className="auth-support-status">{t('common.loading')}</p> : null}
      {error ? <p className="auth-support-status auth-support-status--error">{error}</p> : null}

      {!loading && showIm ? (
        <div className="auth-support-im-card">
          <div>
            <p className="auth-support-im-card__title">客服中心</p>
            <p className="auth-support-im-card__desc">即時線上客服</p>
          </div>
          <button type="button" className="auth-support-card__btn" onClick={startImSupport}>
            開始對話
          </button>
        </div>
      ) : null}

      {!loading && showCms && !error && displayItems.length === 0 ? (
        <p className="auth-support-status">{t('support.empty')}</p>
      ) : null}

      {!loading && showCms
        ? displayItems.map((item) => (
            <div key={item.id} className="auth-support-card">
              <div className="auth-support-card__avatar" aria-hidden>
                {item.image ? (
                  <img src={resolveMemberMediaUrl(item.image)} alt="" className="auth-support-card__icon" />
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                    <path
                      d="M5 20c0-3.3 3.1-6 7-6s7 2.7 7 6"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </div>
              <span className="auth-support-card__label">{item.name || t('support.defaultName')}</span>
              <button
                type="button"
                className="auth-support-card__btn"
                onClick={() => handleContact(item)}
              >
                {item.buttonName?.trim() || t('support.contactNow')}
              </button>
            </div>
          ))
        : null}

      {!loading && showCms && centerImage ? (
        <div className="auth-support-banner">
          <img src={resolveMemberMediaUrl(centerImage)} alt="" className="auth-support-banner__image" />
        </div>
      ) : null}

      {showOff ? (
        centerImage ? (
          <div className="auth-support-banner">
            <img src={resolveMemberMediaUrl(centerImage)} alt="" className="auth-support-banner__image" />
          </div>
        ) : (
          <div className="auth-support-empty">
            <p className="auth-support-empty__hint">{t('support.offlineHint')}</p>
          </div>
        )
      ) : null}
    </AuthSubPageShell>
  );
}
